/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption, Stack } from '@fluentui/react';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { ResourceAssignmentDto } from 'src/core/services/resourceAssignmentSwaggers';
import { logButtonClick, logButtonSuccessfulResourceAssignmentClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { CustomSkillProjectKind } from 'src/core/stores/app/types';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { AddResourceAssignmentModal } from 'src/modules/modelDeploy/AddResourceAssignmentModal';
import { RegionDeploymentsTable } from 'src/modules/modelDeploy/RegionDeploymentsTable';
import { RegionsCommandBar } from 'src/modules/modelDeploy/RegionsCommandBar';
import { RemoveResourceAssignmentModal } from 'src/modules/modelDeploy/RemoveResourceAssignmentModal';
import styled from 'styled-components';

type RegionsTabProps = {
    projectType: CustomSkillProjectKind;
    isServiceKeyTypeValid?: boolean;
    isOrchestrationProjectConnectedToLUISOrQna?: boolean;
    regionDeploymentStoreHouse: ResourceStoreHouse<ResourceAssignmentStore>;
    onAddResourceAssignment: (assignmentData: ResourceAssignmentDto, tracker: ProgressTrackerStore) => void;
    onRemoveResourceAssignment: (assignmentData: Partial<ResourceAssignmentStore>, tracker: ProgressTrackerStore) => void;
    onEditResourceAssignment: (assignmentData: ResourceAssignmentDto, oldResourceId: string, tracker: ProgressTrackerStore) => void;
};

const messages = defineMessages({
    emptyStateMessage: {
        id: 'modelDeployLayout.regionsTab.emptyStateMessage',
        defaultMessage: 'No Resource Assignments available.'
    }
});

const TableContainer = styled(Stack)({ overflow: 'hidden' });

export const RegionsTab = observer((props: RegionsTabProps) => {
    const {
        onAddResourceAssignment,
        onEditResourceAssignment,
        onRemoveResourceAssignment,
        regionDeploymentStoreHouse,
        isServiceKeyTypeValid = true,
        isOrchestrationProjectConnectedToLUISOrQna = false
    } = props;

    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();
    const { selection, selectedItems } = useSelection<ResourceAssignmentStore>();

    const [query, setQuery] = React.useState('');
    const [sortKey, setSortKey] = React.useState<SortKeyType<ResourceAssignmentStore>>({
        key: 'resourceName',
        order: 'asc'
    });

    const [modalStates, updateModalStates] = useUpdateState({
        isAddAssignmentModalHidden: true,
        isEditAssignmentModalHidden: true,
        isRemoveAssignmentModalHidden: true
    });

    const addAssignmentTracker = useProgressTracker({
        onComplete: () => updateModalStates({ isAddAssignmentModalHidden: true })
    });
    const editAssignmentTracker = useProgressTracker({
        onComplete: () => updateModalStates({ isEditAssignmentModalHidden: true })
    });
    const removeAssignmentTracker = useProgressTracker({
        onComplete: () => updateModalStates({ isRemoveAssignmentModalHidden: true })
    });

    const isInEmptyState = regionDeploymentStoreHouse.tracker.isCompleted && !regionDeploymentStoreHouse.items.length;
    const assignedResources = regionDeploymentStoreHouse.items.filter(item => Boolean(item.azureResourceId));

    const projectIdForTelemetry = rootStore.currentCluProjectStore?.id ?? rootStore.currentCustomTextProjectStore?.id;

    const filteredItems = orderBy(
        substringSearchStrategy(assignedResources, query, ['regionName', 'subscriptionName', 'resourceName']),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );

    // move the default resource to be the first element of the table.
    filteredItems.unshift(filteredItems.splice(filteredItems.indexOf(filteredItems.find(item => item.isDefaultResource)), 1)[0]);

    const supportedRegionOptions: IDropdownOption[] = React.useMemo(
        () =>
            regionDeploymentStoreHouse.items
                .filter(item => !Boolean(item.azureResourceId) || selectedItems?.[0]?.id === item.id)
                .map(item => ({ key: item.id, text: item.regionName })),
        [regionDeploymentStoreHouse.items, selectedItems]
    );

    React.useEffect(() => {
        selection.canSelectItem = region => !(region as ResourceAssignmentStore).isDefaultResource;
    }, []);

    const handleAddResourceClick = () => {
        logButtonClick(serviceName, 'DeployModel', 'AssignResource', 'Regions', projectIdForTelemetry);
        updateModalStates({ isAddAssignmentModalHidden: false });
    };

    const handleEditResourceClick = () => {
        logButtonClick(serviceName, 'DeployModel', 'EditAssignment', 'Regions', projectIdForTelemetry);
        updateModalStates({ isEditAssignmentModalHidden: false });
    };

    const handleRemoveAssignmentClick = () => {
        logButtonClick(serviceName, 'DeployModel', 'RemoveAssignment', 'Regions', projectIdForTelemetry);
        updateModalStates({ isRemoveAssignmentModalHidden: false });
    };

    const handleSubmitAddResource = (data: object) => {
        const assignmentData = data as ResourceAssignmentDto;
        onAddResourceAssignment(assignmentData, addAssignmentTracker);

        logButtonSuccessfulResourceAssignmentClick(
            serviceName,
            'DeployModel',
            'AssignResource',
            'Regions',
            projectIdForTelemetry,
            assignmentData
        );
    };

    const handleSubmitEditResource = (data: object) => {
        const assignmentData = data as ResourceAssignmentDto;
        onEditResourceAssignment(data as ResourceAssignmentDto, selectedItems?.[0]?.azureResourceId, editAssignmentTracker);

        logButtonSuccessfulResourceAssignmentClick(
            serviceName,
            'DeployModel',
            'EditResourceAssignment',
            'Regions',
            projectIdForTelemetry,
            assignmentData
        );
    };

    const handleSubmitDeleteResource = () => {
        onRemoveResourceAssignment(selectedItems?.[0], removeAssignmentTracker);
        const assignmentData = selectedItems?.[0] as ResourceAssignmentDto;

        logButtonSuccessfulResourceAssignmentClick(
            serviceName,
            'DeployModel',
            'RemoveResourceAssignment',
            'Regions',
            projectIdForTelemetry,
            assignmentData
        );
    };

    return (
        <>
            <TableContainer verticalFill tokens={{ childrenGap: 5, padding: '5px 0' }}>
                <RegionsCommandBar
                    onQueryChange={setQuery}
                    selectedItems={selectedItems}
                    isServiceKeyTypeValid={isServiceKeyTypeValid}
                    onAssignResourceButtonClick={handleAddResourceClick}
                    onEditAssignmentButtonClick={handleEditResourceClick}
                    onRemoveAssignmentButtonClick={handleRemoveAssignmentClick}
                    isOrchestrationProjectConnectedToLUISOrQna={isOrchestrationProjectConnectedToLUISOrQna}
                />

                {isInEmptyState ? (
                    <EmptyStateView imageStyles={{ image: { height: 150 } }} message={localize(messages.emptyStateMessage)} />
                ) : (
                    <RegionDeploymentsTable
                        sortKey={sortKey}
                        selection={selection}
                        items={filteredItems}
                        areItemsFiltered={Boolean(query)}
                        overallItemsCount={filteredItems.length}
                        isLoading={regionDeploymentStoreHouse.tracker.isRunning}
                        onSortColumn={column => {
                            selection.setAllSelected(false);
                            setSortKey({
                                key: column.key as keyof ResourceAssignmentStore,
                                order: column.isSortedDescending ? 'asc' : 'desc'
                            });
                        }}
                    />
                )}
            </TableContainer>

            <AddResourceAssignmentModal
                onSubmit={handleSubmitAddResource}
                regionOptions={supportedRegionOptions}
                disabled={addAssignmentTracker.isRunning}
                hidden={modalStates.isAddAssignmentModalHidden}
                onDismiss={() => {
                    updateModalStates({ isAddAssignmentModalHidden: true });
                    addAssignmentTracker.reset();
                }}
            />

            <AddResourceAssignmentModal
                isEditAssignment
                onSubmit={handleSubmitEditResource}
                regionOptions={supportedRegionOptions}
                selectedRegionId={selectedItems?.[0]?.id}
                disabled={editAssignmentTracker.isRunning}
                hidden={modalStates.isEditAssignmentModalHidden}
                selectedResourceId={selectedItems?.[0]?.azureResourceId}
                selectedSubscriptionId={selectedItems?.[0]?.subscriptionId}
                onDismiss={() => {
                    updateModalStates({ isEditAssignmentModalHidden: true });
                    editAssignmentTracker.reset();
                }}
            />

            <RemoveResourceAssignmentModal
                onSubmit={handleSubmitDeleteResource}
                disabled={removeAssignmentTracker.isRunning}
                hidden={modalStates.isRemoveAssignmentModalHidden}
                selectedRegionName={selectedItems?.[0]?.regionName}
                selectedResourceName={selectedItems?.[0]?.resourceName}
                onDismiss={() => {
                    updateModalStates({ isRemoveAssignmentModalHidden: true });
                    removeAssignmentTracker.reset();
                }}
            />
        </>
    );
});
